import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import { getPostBySlug, sortedPosts } from "@/data/blog";

const SITE_URL = "https://episodekit.net";

const useJsonLd = (jsonLd) => {
  useEffect(() => {
    if (!jsonLd) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-jsonld", "blog-post");
    el.text = JSON.stringify(jsonLd);
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [JSON.stringify(jsonLd)]);
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  const seoTitle =
    post?.seo?.title || "Blog | EpisodeKit";
  const seoDescription =
    post?.seo?.description ||
    "Podcast SEO and distribution writing from the EpisodeKit team.";
  const seoPath = post ? `/blog/${post.slug}` : "/blog";

  useSEO({
    title: seoTitle,
    description: seoDescription,
    path: seoPath,
    image: post?.seo?.ogImage,
  });

  useJsonLd(
    post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
              {
                "@type": "ListItem",
                position: 3,
                name: post.title,
                item: `${SITE_URL}/blog/${post.slug}`,
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE_URL}/blog/${post.slug}`,
            },
            headline: post.title,
            description: post.seo.description,
            image: post.seo.ogImage
              ? `${SITE_URL}${post.seo.ogImage}`
              : undefined,
            datePublished: post.publishedAt,
            dateModified: post.dateModified || post.publishedAt,
            author: {
              "@type": "Organization",
              name: post.author?.name || "EpisodeKit",
              url: `${SITE_URL}/about`,
            },
            publisher: {
              "@type": "Organization",
              name: "EpisodeKit",
              url: SITE_URL,
              logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/logo-icon.png`,
              },
            },
          },
          ...(Array.isArray(post.extraJsonLd) ? post.extraJsonLd : []),
        ]
      : null,
  );

  if (!post) return <Navigate to="/blog" replace />;

  const others = sortedPosts().filter((p) => p.slug !== post.slug).slice(0, 3);
  const Body = post.Body;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid={`blog-post-${post.slug}`}
    >
      <Navbar />

      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-neutral-500 mb-8 flex items-center gap-2"
          >
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span aria-hidden="true">/</span>
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-neutral-300 truncate max-w-[60vw]">
              {post.title}
            </span>
          </nav>

          {/* Header */}
          <header className="mb-10 md:mb-12">
            <div className="flex items-center flex-wrap gap-2 mb-5">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide text-purple-200 bg-purple-500/15 border border-purple-500/30 uppercase">
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.publishedAt)}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                <Clock className="w-3.5 h-3.5" />
                {post.readingTime}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-unbounded font-bold leading-[1.05] mb-6">
              {post.title}
            </h1>
            <p className="text-neutral-400">
              By{" "}
              <Link
                to="/about"
                className="text-neutral-200 font-medium hover:text-white underline underline-offset-4 decoration-neutral-700 hover:decoration-purple-500/60 transition-colors"
                data-testid="blog-byline-link"
              >
                {post.author?.name || "EpisodeKit"}
              </Link>
              {post.author?.role && (
                <>
                  <span className="text-neutral-700 mx-2">·</span>
                  <span className="text-neutral-500">{post.author.role}</span>
                </>
              )}
            </p>
          </header>

          {/* Table of contents */}
          {post.toc && post.toc.length > 0 && (
            <aside
              className="mb-12 rounded-xl border border-neutral-800 bg-neutral-900/40 p-5"
              aria-label="Table of contents"
            >
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">
                In this article
              </p>
              <ol className="space-y-2">
                {post.toc.map((item, i) => (
                  <li key={item.id} className="text-sm">
                    <a
                      href={`#${item.id}`}
                      className="text-neutral-300 hover:text-white transition-colors"
                    >
                      <span className="text-neutral-600 mr-2">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>
          )}

          {/* Body */}
          <article className="prose-blog" data-testid="blog-post-body">
            <Body />
          </article>

          {/* Footer / next reads */}
          <section className="mt-20 pt-10 border-t border-neutral-800">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-700 hover:border-purple-500/60 rounded-full px-4 py-2.5 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                All articles
              </Link>
              <Link to="/register">
                <button className="btn-primary inline-flex items-center gap-2 text-sm px-6 py-3">
                  Try EpisodeKit Free
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>

            {others.length > 0 && (
              <>
                <h2 className="text-xl font-unbounded font-semibold mb-5">
                  Keep reading
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {others.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/blog/${p.slug}`}
                      className="block p-5 rounded-xl border border-neutral-800 hover:border-purple-500/40 bg-neutral-900/40 transition-colors"
                    >
                      <p className="text-[10px] uppercase tracking-widest text-purple-300 font-semibold mb-2">
                        {p.category}
                      </p>
                      <p className="text-white font-semibold leading-snug">
                        {p.title}
                      </p>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
